import { useEffect, useRef } from 'react';
import { LayoutDashboard, Map, List, Info, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';

export type MobileTab = 'map' | 'list' | 'detail';

interface MobileTabNavigationProps {
  activeTab: MobileTab;
  onTabChange: (tab: MobileTab) => void;
  hasSelectedUnit: boolean;
  isAdmin?: boolean;
}

export function MobileTabNavigation({ activeTab, onTabChange, hasSelectedUnit, isAdmin = false }: MobileTabNavigationProps) {
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const updateNavHeight = () => {
      if (navRef.current) {
        const height = navRef.current.offsetHeight;
        document.documentElement.style.setProperty('--mobile-nav-height', `${height}px`);
      }
    };

    // Set initial height
    updateNavHeight();

    // Update on resize
    window.addEventListener('resize', updateNavHeight);

    return () => {
      window.removeEventListener('resize', updateNavHeight);
      // Clean up CSS custom property on unmount
      document.documentElement.style.removeProperty('--mobile-nav-height');
    };
  }, []);

  return (
    <nav
      ref={navRef}
      data-mobile-nav
      className="mobile-tab-navigation fixed bottom-0 left-0 right-0 z-20 bg-[#8FB560] border-t border-[#7A9A52] pb-0"
    >
      <div className="flex items-center justify-around h-16">
        {isAdmin ? (
          <a
            href="/admin"
            className="flex flex-col items-center justify-center flex-1 h-full transition-all text-gray-800 hover:bg-black/5"
          >
            <Shield className="h-5 w-5 mb-1" />
            <span className="text-xs font-medium">Admin</span>
          </a>
        ) : (
          <a
            href="/dashboard"
            className="flex flex-col items-center justify-center flex-1 h-full transition-all text-gray-800 hover:bg-black/5"
          >
            <LayoutDashboard className="h-5 w-5 mb-1" />
            <span className="text-xs font-medium">Dashboard</span>
          </a>
        )}

        <button
          onClick={() => onTabChange('map')}
          className={cn(
            'flex flex-col items-center justify-center flex-1 h-full transition-all',
            activeTab === 'map'
              ? 'text-gray-900 bg-black/10'
              : 'text-gray-800 hover:bg-black/5'
          )}
        >
          <Map className="h-5 w-5 mb-1" />
          <span className="text-xs font-medium">Map</span>
        </button>

        <button
          onClick={() => onTabChange('list')}
          className={cn(
            'flex flex-col items-center justify-center flex-1 h-full transition-all',
            activeTab === 'list'
              ? 'text-gray-900 bg-black/10'
              : 'text-gray-800 hover:bg-black/5'
          )}
        >
          <List className="h-5 w-5 mb-1" />
          <span className="text-xs font-medium">List</span>
        </button>

        <button
          onClick={() => onTabChange('detail')}
          disabled={!hasSelectedUnit}
          className={cn(
            'flex flex-col items-center justify-center flex-1 h-full transition-all',
            activeTab === 'detail' && hasSelectedUnit
              ? 'text-gray-900 bg-black/10'
              : 'text-gray-800 hover:bg-black/5',
            !hasSelectedUnit && 'opacity-40 cursor-not-allowed'
          )}
        >
          <Info className="h-5 w-5 mb-1" />
          <span className="text-xs font-medium">Detail</span>
        </button>
      </div>
    </nav>
  );
}
